import { Build, Component, h, Host, State, Prop, Element, Listen } from '@stencil/core'
import { container, CitrusadService, CitrusadBanner, ListingProduct } from '@rdss/client'
import { trackCitrusAd } from '~/utils'

/**
 * Zoo BannerX.
 *
 * Bin/console ui:widgets:generate.
 */
@Component({
    tag: 'zr-bannerx-wrapper',
    styleUrl: 'zr-bannerx-wrapper.scss',
    shadow: true,
})

/**
 * ZooRoyal Banner X Wrapper.
 */
export class ZrBannerxWrapper {
    /**
     * Element reference.
     */
    @Element() host: HTMLElement

    /**
     * Category property (1 is homepage).
     */
    @Prop() category = 1

    /**
     * Search term property (set in search context).
     */
    @Prop() searchTerm = ''

    /**
     * Banner X Mock Information property.
     */
    @State() citrusadBannerData: CitrusadBanner

    private validHosts = [
        'hydrate.stenciljs.com',
        '.rdss.it',
        '.de',
        '.at',
    ]

    private readonly citrusad = container.resolve(CitrusadService)

    private loadBanner = async () => {
        return this.citrusad.getBanner(this.category, this.searchTerm)
    }

    /**
     * Regular expression to check if the pathname is a category page.
     */
    private categoryPageRegExp = new RegExp('^\\/[^/]+\\/$')

    /**
     * Checks if the host is valid.
     */
    private isValidHost() {
        return this.validHosts.some(host => window.location.hostname.endsWith(host))
    }

    /**
     * Checks pathname is a category page.
     */
    private isCategoryPage() {
        return this.categoryPageRegExp.test(location.pathname)
    }

    /**
     * Lifecycle method to set the margin bottom and border radius.
     */
    async componentDidLoad() {
        const response = await this.loadBanner() as unknown as CitrusadBanner
        if (response) {
            this.citrusadBannerData = response
            if (Build.isBrowser) {
                this.citrusadBannerData.products = this.citrusadBannerData?.products.map((product) => {
                    product.sponsored = true
                    product.citrusAdId = this.citrusadBannerData?.bannerx?.citrusAdId
                    trackCitrusAd('impression', product)
                    return product
                })
            }
            this.host.style.setProperty('--zr-bannerx-text-color', `#${this.citrusadBannerData?.bannerx?.bannerTextColour}`)
            this.host.style.setProperty('--zr-bannerx-background-color', `#${this.citrusadBannerData?.bannerx?.backgroundColour}`)
            this.host.style.setProperty('--zr-bannerx-display', 'block')
        }
        if (Build.isBrowser) {
            this.host.style.setProperty('--zr-bannerx-visibility', 'visible')
        }
    }

    /**
     * Handle add to cart click.
     */
    @Listen('addToCartClicked')
    handleAddCartClick({ detail } : CustomEvent<{ quantity: number, productId: number, context: string, increased: boolean }>) {
        if (detail.quantity > 0) {
            console.log('BANNERX: Add to cart clicked', detail)
        }
    }

    /**
     * Handle product click.
     */
    @Listen('productClicked')
    handleProductClick({ detail } : CustomEvent<{ product: ListingProduct }>) {
        // this will also call the pdp and potentially "eat" any request made here
        // track clicks on pdp by passing tracking id into parameter, e.g. citrusadid
        console.log('BANNERX: product clicked', detail)
    }

    /**
     * Renders the component.
     */
    render() {
        return (
            this.citrusadBannerData &&
            this.citrusadBannerData.products &&
            this.citrusadBannerData.products.length > 0 &&
            this.isValidHost() &&
            !this.isCategoryPage()) && (
            <Host>
                <section aria-label={this.citrusadBannerData.bannerx.headingText} class="zr-bannerx-wrapper">
                    <zr-bannerx-hero
                        ariaLabel={this.citrusadBannerData.bannerx.heroImageAltText}
                        mobileImage={this.citrusadBannerData.bannerx.secondaryBackgroundImage}
                        backgroundImage={this.citrusadBannerData.bannerx.backgroundImage}
                        ctaLink={this.citrusadBannerData.bannerx.ctaLink}
                        ctaText={this.citrusadBannerData.bannerx.ctaText}
                    />
                    <div class="zr-bannerx-wrapper__content">
                        <div class="zr-bannerx-wrapper__head">
                            <div class="zr-bannerx-wrapper__headline" innerHTML={this.citrusadBannerData.bannerx.bannerText}/>
                            <zr-bannerx-slider-control />
                        </div>
                        <zr-bannerx-slider
                            citrusAdId={this.citrusadBannerData.bannerx.citrusAdId}
                            products={this.citrusadBannerData.products}
                            ctaText={this.citrusadBannerData.bannerx.ctaText}
                            ctaLink={this.citrusadBannerData.bannerx.ctaLink}
                            ctaTextAccessibility={this.citrusadBannerData.bannerx.ctaTextAccessibility}
                        />
                    </div>
                </section>
            </Host>
        )
    }
}
